import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/auth/store';
import { useReplayOnReconnect } from '@/lib/offline/useReplayOnReconnect';
import { EmailTestModeBanner } from './EmailTestModeBanner';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

/**
 * AppShell — authenticated layout shell.
 *
 * Composition: Sidebar (responsive) + Topbar + main outlet. Mounted
 * inside ProtectedRoute so we can safely assume `user` is non-null
 * (the guard already redirected to /login otherwise).
 *
 * Mobile drawer: Sidebar exposes an `open` prop wired to local state.
 * The drawer auto-closes whenever the route changes so navigating to
 * a sibling page doesn't leave the overlay stuck open.
 */
export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Replay queued mutations whenever the browser reports it's online.
  useReplayOnReconnect();

  if (!user) return null;

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar
        user={user}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <EmailTestModeBanner />
        <Topbar user={user} onOpenSidebar={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
